import { occupancyOverlap } from '../../../../ft3d/engine/ft3d-move-core.js';
import { combatSizeCreditFt, combatVerticalSeparationFt } from '../../../../ft3d/engine/ft3d-combat-distance-core.js';
import { getSwarmTemplateForCreature, swarmOccupiedWidthFt, swarmVRatio01, swarmWidthLanesForCreature } from '../../shared/swarm-templates.js';
import type { EncounterCreatureRow } from '../../shared/array-types';
import type { Zone } from '../inventory/floor-move';

/** Rules size and height stay individual-sized; swarm frontage lies on world X. */
export interface MovementBody { diameter: number; height: number; rulesDiameter?: number; widthFt?: number; facing?: number }
export function swarmMovementBody(row: Record<string, unknown>, body: MovementBody): MovementBody {
    const creature = row as unknown as EncounterCreatureRow;
    const template = getSwarmTemplateForCreature(creature);
    if (!template) return body;
    const base = body.diameter;
    const widthFt = swarmOccupiedWidthFt(base, template, swarmWidthLanesForCreature(creature, template), swarmVRatio01(creature));
    return widthFt > base + 1e-6 ? { ...body, widthFt } : body;
}
export function movementFootprint(body: MovementBody, x: number, y: number, z: number): Zone {
    return body.widthFt !== undefined
        ? { kind: 'oblong', x, y, halfW: body.widthFt / 2, halfD: body.diameter / 2, facing: body.facing ?? 0, zMin: z, zMax: z + body.height }
        : { kind: 'circle', x, y, r: body.diameter / 2, zMin: z, zMax: z + body.height };
}
export const movementRadius = (body: MovementBody): number => Math.max(body.diameter, body.widthFt ?? 0) / 2;
export function movementOverlap(a: MovementBody, at: { x: number; y: number; z: number }, b: MovementBody,
    bt: { x: number; y: number; z: number }): boolean {
    return occupancyOverlap(movementFootprint(a, at.x, at.y, at.z), movementFootprint(b, bt.x, bt.y, bt.z));
}
/** Combat keeps the individual rules size. A source swarm extends its source
 * centres along X; a target swarm exposes its rectangular frontage rim. Neither
 * widens the vertical span or changes the existing excess-over-5 source credit. */
function reachWidthFt(body: MovementBody): number {
    const size = body.rulesDiameter ?? body.diameter;
    // Placement may use a physical mesh diameter. Carry its lane count onto the
    // rules diameter, exactly as swarmMovementBody does for the reaction reader.
    return body.widthFt === undefined ? size : size + (body.widthFt - body.diameter) / Math.min(body.diameter, 5) * Math.min(size, 5);
}
export const movementReachRadius = (body: MovementBody): number => reachWidthFt(body) / 2;
function reachFootprint(source: MovementBody, target: MovementBody): { w: number; d: number; r: number; credit: number } {
    const sourceSize = source.rulesDiameter ?? source.diameter, targetSize = target.rulesDiameter ?? target.diameter;
    return { w: Math.max(0, reachWidthFt(source) - sourceSize) / 2 + (target.widthFt === undefined ? 0 : reachWidthFt(target) / 2),
        d: target.widthFt === undefined ? 0 : targetSize / 2, r: target.widthFt === undefined ? targetSize / 2 : 0,
        credit: combatSizeCreditFt(sourceSize) };
}
export function movementReachDistanceFt(source: MovementBody, at: { x: number; y: number; z: number }, target: MovementBody,
    bt: { x: number; y: number; z: number }): number {
    const { w, d, r, credit } = reachFootprint(source, target);
    const planar = Math.max(0, Math.hypot(Math.max(0, Math.abs(bt.x - at.x) - w), Math.max(0, Math.abs(bt.y - at.y) - d)) - r);
    const vertical = combatVerticalSeparationFt(bt.z - at.z, source.rulesDiameter ?? source.diameter, target.rulesDiameter ?? target.diameter);
    return Math.max(0, Math.hypot(planar, vertical) - credit);
}
/** Same swept reach boundary for walk admission, consent and the OA leave point.
 * Sloped segments retain the existing conservative closest-height credit. */
export function movementReachContact(source: MovementBody, at: { x: number; y: number; z: number }, reach: number,
    target: MovementBody, path: ReadonlyArray<readonly number[]>): { touched: boolean; left: boolean; leaveIndex: number | null } {
    const { w, d, r, credit } = reachFootprint(source, target);
    let touched = false, left = false, leaveIndex: number | null = null;
    for (let i = 1; i < path.length; i++) {
        const a = path[i - 1]!, b = path[i]!;
        const low = Math.min(a[2]!, b[2]!), high = Math.max(a[2]!, b[2]!);
        const dz = at.z < low ? at.z - low : at.z > high ? at.z - high : 0;
        const gap = combatVerticalSeparationFt(dz, source.rulesDiameter ?? source.diameter, target.rulesDiameter ?? target.diameter);
        const limit = reach + credit;
        if (gap > limit) continue;
        const planar = r + Math.sqrt(Math.max(0, limit * limit - gap * gap));
        if (segmentRectangleDistance2(a, b, at.x, at.y, w, d) > planar * planar) continue;
        touched = true;
        if (movementReachDistanceFt(source, at, target, { x: b[0]!, y: b[1]!, z: b[2]! }) > reach) {
            left = true; leaveIndex ??= i - 1;
        }
    }
    return { touched, left, leaveIndex };
}
/** Exact flat sweep of size discs / swarm rectangles. The Minkowski
 * sum is a rectangle with rounded corners; split where the nearest edge changes. */
export function movementCrosses(a: MovementBody, from: readonly number[], to: readonly number[], b: MovementBody, x: number, y: number): boolean {
    if ((a.widthFt !== undefined && a.facing) || (b.widthFt !== undefined && b.facing)) {
        if (a.widthFt === undefined || b.widthFt === undefined) {
            const box = a.widthFt === undefined ? b : a;
            const angle = -(box.facing ?? 0), c = Math.cos(angle), s = Math.sin(angle);
            const local = (p: readonly number[]): number[] => [c * (p[0]! - x) - s * (p[1]! - y), s * (p[0]! - x) + c * (p[1]! - y)];
            const r = (a.widthFt === undefined ? a.diameter : b.diameter) / 2;
            return segmentRectangleDistance2(local(from), local(to), 0, 0, box.widthFt! / 2, box.diameter / 2) < r * r - 1e-6;
        }
        // Swept separating axes for two rectangles with fixed orientations.
        let enter = 0, leave = 1;
        const extent = (body: MovementBody, axis: number): number => Math.abs(Math.cos((body.facing ?? 0) - axis)) * body.widthFt! / 2
            + Math.abs(Math.sin((body.facing ?? 0) - axis)) * body.diameter / 2;
        for (const axis of [a.facing ?? 0, (a.facing ?? 0) + Math.PI / 2, b.facing ?? 0, (b.facing ?? 0) + Math.PI / 2]) {
            const c = Math.cos(axis), s = Math.sin(axis), limit = extent(a, axis) + extent(b, axis) - 1e-6;
            const p = (from[0]! - x) * c + (from[1]! - y) * s, v = (to[0]! - from[0]!) * c + (to[1]! - from[1]!) * s;
            if (Math.abs(v) < 1e-12) { if (Math.abs(p) >= limit) return false; continue; }
            const t1 = (-limit - p) / v, t2 = (limit - p) / v;
            enter = Math.max(enter, Math.min(t1, t2)); leave = Math.min(leave, Math.max(t1, t2));
            if (enter >= leave) return false;
        }
        return true;
    }
    const rect = (body: MovementBody): [number, number] => body.widthFt === undefined ? [0, 0] : [body.widthFt / 2, body.diameter / 2];
    const [aw, ad] = rect(a), [bw, bd] = rect(b), w = aw + bw, d = ad + bd;
    const r = (a.widthFt === undefined ? a.diameter / 2 : 0) + (b.widthFt === undefined ? b.diameter / 2 : 0);
    if (r > 0) return segmentRectangleDistance2(from, to, x, y, w, d) < r * r - 1e-6;
    // Strict interior overlap: touching rectangular edges alone is clear.
    return segmentRectangleDistance2(from, to, x, y, Math.max(0, w - 1e-6), Math.max(0, d - 1e-6)) === 0;
}
/** Minimum squared distance from a segment to an axis-aligned rectangle. */
function segmentRectangleDistance2(from: readonly number[], to: readonly number[], x: number, y: number, w: number, d: number): number {
    const px = from[0]! - x, py = from[1]! - y, dx = to[0]! - from[0]!, dy = to[1]! - from[1]!;
    const cuts = [0, 1];
    for (const [p, v, half] of [[px, dx, w], [py, dy, d]]) if (v !== 0)
        for (const side of [-half!, half!]) { const t = (side - p!) / v!; if (t > 0 && t < 1) cuts.push(t); }
    cuts.sort((a, b) => a - b);
    const distance2 = (t: number): number => Math.max(0, Math.abs(px + dx * t) - w) ** 2 + Math.max(0, Math.abs(py + dy * t) - d) ** 2;
    let closest = Infinity;
    for (let i = 1; i < cuts.length; i++) {
        const lo = cuts[i - 1]!, hi = cuts[i]!, mid = (lo + hi) / 2;
        const outsideX = Math.abs(px + dx * mid) > w, outsideY = Math.abs(py + dy * mid) > d;
        const vx = outsideX ? dx : 0, vy = outsideY ? dy : 0;
        const ox = outsideX ? px - Math.sign(px + dx * mid) * w : 0, oy = outsideY ? py - Math.sign(py + dy * mid) * d : 0;
        const denom = vx * vx + vy * vy;
        const t = denom ? Math.max(lo, Math.min(hi, -(ox * vx + oy * vy) / denom)) : mid;
        closest = Math.min(closest, distance2(t));
    }
    return closest;
}
